import { db } from '../../db/indexeddb'
import {
  getCloudSubtasks,
  getCloudSubtaskIds,
  getCloudSubtasksByIds,
  getDeletedCloudSubtasks,
  createCloudSubtask,
  updateCloudSubtask,
  deleteCloudSubtask,
} from '../../supabase/queries'
import { CloudSubtaskSchema, CloudSubtaskPayloadSchema } from '../sync-schemas'
import { resolveCloudTaskId, resolveLocalTaskId } from '../sync-resolve'
import type { PullDescriptor } from '../sync-pull'
import type { PushDescriptor } from '../sync-push'
import type { Subtask } from '../../db/models'

/* ── SUBTASKS ─────────────────────────────────────────── */

export const subtaskPullDescriptor: PullDescriptor<any> = {
  entityName: 'subtask',
  settingsKey: 'lastSubtaskSyncTime',

  fetchRemote: (userId, since) => getCloudSubtasks(userId, since),
  fetchRemoteIds: (userId) => getCloudSubtaskIds(userId),
  fetchRemoteByIds: (userId, ids) => getCloudSubtasksByIds(userId, ids),
  fetchDeletedRemote: (userId, since) => getDeletedCloudSubtasks(userId, since),

  safeParse: (raw) => CloudSubtaskSchema.safeParse(raw),
  getRemoteId: (r) => r.id,
  getRemoteTimestamp: (r) => r.updated_at,
  getRemoteLocalId: (r) => r.local_id,

  table: db.subtasks,
  getAllLocal: () => db.subtasks.toArray(),

  // Leer ist die Tabelle auch nach der Umwandlung in Aufgaben (T14 #179) — dann ist das
  // Gerät nicht frisch, und ein Voll-Abgleich bei jedem Holen wäre Verschwendung.
  shouldForceFullSync: async () => {
    const count = await db.subtasks.filter(s => !s.deletedAt).count()
    if (count > 0) return false
    return (await db.tasks.where('parentTaskId').above('').count()) === 0
  },

  buildLocalInsert: async (remote) => {
    const localTaskId = await resolveLocalTaskId(remote.task_id, remote.task_local_id)
    // Parent task not on this device yet — the next cycle retries, by which
    // time the task pull (which runs before subtasks) will have created it.
    if (!localTaskId) return null
    return {
      id: crypto.randomUUID(),
      taskId: localTaskId,
      title: remote.title,
      done: remote.done,
      sortOrder: remote.sort_order,
      userId: remote.user_id,
      cloudId: remote.id,
      deletedAt: null,
      importedFrom: remote.imported_from ?? null,
      createdAt: new Date(remote.created_at),
      updatedAt: new Date(remote.updated_at),
      lastModified: new Date(remote.updated_at),
      synced: true,
      syncedAt: new Date(),
    }
  },

  buildLocalUpdate: async (remote) => ({
    title: remote.title,
    done: remote.done,
    sortOrder: remote.sort_order,
    ...('imported_from' in remote ? { importedFrom: remote.imported_from ?? null } : {}),
    updatedAt: new Date(remote.updated_at),
    ...(remote.deleted_at ? { deletedAt: new Date(remote.deleted_at) } : { deletedAt: null }),
  }),
}

export const subtaskPushDescriptor: PushDescriptor<Subtask> = {
  entityName: 'CloudSubtask',
  getLocal: async (id) => (await db.subtasks.get(id)) ?? null,
  getLocalCloudId: (l) => l.cloudId,
  getLocalId: (l) => l.id,
  table: db.subtasks,

  // A subtask cannot exist in the cloud before its task does; defer until the
  // task push has handed back a cloud id.
  canCreate: async (subtask) => {
    const cloudTaskId = await resolveCloudTaskId(subtask.taskId)
    return cloudTaskId ? { defer: false } : { defer: true }
  },

  canUpdate: async (subtask) => {
    const cloudTaskId = await resolveCloudTaskId(subtask.taskId)
    return cloudTaskId ? { defer: false } : { defer: true }
  },

  buildCreatePayload: async (subtask) => {
    const cloudTaskId = await resolveCloudTaskId(subtask.taskId)
    return CloudSubtaskPayloadSchema.parse({
      task_id: cloudTaskId,
      task_local_id: subtask.taskId,
      title: subtask.title,
      done: subtask.done,
      sort_order: subtask.sortOrder,
      local_id: subtask.id,
      imported_from: subtask.importedFrom ?? null,
    })
  },

  buildUpdatePayload: async (subtask) => {
    const cloudTaskId = await resolveCloudTaskId(subtask.taskId)
    const payload: Record<string, any> = CloudSubtaskPayloadSchema.parse({
      task_id: cloudTaskId,
      task_local_id: subtask.taskId,
      title: subtask.title,
      done: subtask.done,
      sort_order: subtask.sortOrder,
      local_id: subtask.id,
      imported_from: subtask.importedFrom ?? null,
    })
    // Undo re-activates a soft-deleted row — the null has to be sent explicitly
    // or the cloud row stays dead (sync checklist point 17).
    if (!subtask.deletedAt) payload.deleted_at = null
    return payload
  },

  createCloud: (userId, payload) => createCloudSubtask(userId, payload),
  updateCloud: (cloudId, payload, userId) => updateCloudSubtask(cloudId, payload, userId),
  deleteCloud: (cloudId, userId) => deleteCloudSubtask(cloudId, userId),
}
